import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';

@Component({
  selector: 'df-add-recurrence',
  templateUrl: './add-recurrence.component.html',
  styleUrls: ['./add-recurrence.component.scss']
})
export class AddRecurrenceComponent implements OnInit {
  
  @ViewChild('content') content!: ElementRef;
  @Output() submit = new EventEmitter<any>();
  
  selectedEveryPeriod: number = 1;

  periods: string[] = ['week', 'day'];
  selectedPeriod: string = 'week';

  weekdays: string[] = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  selectedDays: boolean[] = [false, false, false, false, false, false, false];

  selectedEndOption: number = 0;

  form!: FormGroup;

  startDate: Date | null = null;
  availableSessions: number = 0;

  selectedSessionDates: Date[] = [];

  constructor(private fb: FormBuilder, private modalService: NgbModal) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      everyperiod: [{ value: 1 }],
      enddate: [{ value: '' }],
      endoccurences: [{ value: 0 }]
    });
  }

  open(date: Date, sessions: number = 0, sessionMin = 60, state = 'week', recOptions: any = undefined): NgbModalRef {
    this.selectedPeriod = state || this.periods[0];
    
    this.selectedEveryPeriod = recOptions ? recOptions.every : 1;
    this.form.controls['everyperiod'].setValue(this.selectedEveryPeriod);

    this.selectedDays = recOptions ? recOptions.days : [false, false, false, false, false, false, false];

    this.startDate = date;
    this.availableSessions = 0;
    if (date) {
      !recOptions && (this.selectedDays[date.getDay()] = true);
      if (sessions > 0) {
        // [sessions] = min bookable occurrences at current duration (from sessionCountForBookingSlot), not minutes.
        this.availableSessions = Math.max(0, Math.floor(sessions));
        if (recOptions) {
          this.setOccurances(recOptions.sessionDates.length);
        } else {
          this.setOccurances(this.availableSessions);
        }
        this.selectedEndOption = 1;

        this.setLastDate(this.calcLastDate());
      }
    }

    return this.modalService.open(this.content);
  }

  getOccurances(): number {
    return parseInt(this.form.controls['endoccurences'].value);
  }
  setOccurances(occurances: number) {
    this.form.controls['endoccurences'].setValue(occurances);
  }

  getLastDate(): Date | null {
    return this.form.controls['enddate'].value;
  }
  setLastDate(date: Date | null) {
    this.form.controls['enddate'].setValue(date);
  }

  calcLastDate(): Date | null {
    let dates = this.getSessionDatesByOcc();
    if (dates.length) return dates[dates.length - 1];
    return null;
  }

  getSessionDatesByOcc(): Date[] {
    let occuranceDates: Date[] = [];
    if (this.startDate && (this.selectedDays.find(d => d) || this.selectedPeriod === 'day')) {
      let occurances = this.getOccurances();
      if (occurances > this.availableSessions) occurances = this.availableSessions;
      let tryDate = this.startDate;

      while (occuranceDates.length < occurances) {
        let dow: number = tryDate.getDay();
        if (this.selectedPeriod === 'day'
          || this.selectedPeriod === 'week' && this.selectedDays[dow]) {
          occuranceDates.push(tryDate);
        }

        // account for 'every'
        if (this.selectedEveryPeriod > 1) {
          if (this.selectedPeriod === 'day')
            tryDate = moment(tryDate).add(this.selectedEveryPeriod - 1, 'days').toDate();
          else if (this.selectedPeriod === 'week' && dow === 0)
            tryDate = moment(tryDate).add(this.selectedEveryPeriod - 1, 'weeks').toDate();
        }
        
        tryDate = moment(tryDate).add(1, 'days').toDate();
      }
    }
    this.selectedSessionDates = occuranceDates;
    return occuranceDates;
  }

  getSessionDatesByDate(): Date[] {
    let occuranceDates: Date[] = [];
    if (this.startDate && (this.selectedDays.find(d => d) || this.selectedPeriod === 'day')) {
      let endDate = this.getLastDate() || new Date();
      let tryDate = this.startDate;

      while (tryDate <= endDate && occuranceDates.length < this.availableSessions) {
        let dow: number = tryDate.getDay();
        if (this.selectedPeriod === 'day'
          || this.selectedPeriod === 'week' && this.selectedDays[dow]) {
          occuranceDates.push(tryDate);
        }

        // account for 'every'
        if (this.selectedEveryPeriod > 1) {
          if (this.selectedPeriod === 'day')
            tryDate = moment(tryDate).add(this.selectedEveryPeriod - 1, 'days').toDate();
          else if (this.selectedPeriod === 'week' && dow === 0)
            tryDate = moment(tryDate).add(this.selectedEveryPeriod - 1, 'weeks').toDate();
        }

        tryDate = moment(tryDate).add(1, 'days').toDate();
      }
    }
    this.selectedSessionDates = occuranceDates;
    return occuranceDates;
  }

  isFormValid(): boolean {
    if (this.selectedEveryPeriod < 1) {
      return false;
    }
    if (this.selectedPeriod === 'week' && !this.selectedDays.some(d => d)) {
      return false;
    }
    if (this.selectedEndOption === 1) {
      const occ = this.getOccurances();
      if (occ < 1 || occ > this.availableSessions) {
        return false;
      }
    }
    if (this.selectedEndOption === 2) {
      const end = this.getLastDate();
      if (!end || !this.startDate) {
        return false;
      }
      if (moment(end).startOf('day').isBefore(moment(this.startDate).startOf('day'))) {
        return false;
      }
    }
    return this.selectedSessionDates.length > 0;
  }

  onSubmit(modal: any) {
    this.submit.emit({
      every: this.selectedEveryPeriod,
      period: this.selectedPeriod,
      days: [...this.selectedDays],
      endOption: this.selectedEndOption,
      sessionDates: [...this.selectedSessionDates],
      recurrenceEndDate: this.selectedEndOption === 2 ? this.getLastDate() : null
    });
    modal.close('success');
  }

  onCancel(modal: any) {
    modal.dismiss('cancel');
  }

  syncEndOptions() {
    if (this.selectedEndOption === 1)
      this.setLastDate(this.calcLastDate());
    else
      this.setOccurances(this.getSessionDatesByDate().length);
  }

  changeEveryPeriod() {
    setTimeout(() => {
      this.selectedEveryPeriod = parseInt(this.form.controls['everyperiod'].value);
      if (this.selectedEveryPeriod > 7) this.selectedEveryPeriod = 7;
      if (this.selectedEveryPeriod < 1) this.selectedEveryPeriod = 1;
      this.syncEndOptions();
    });
  }

  selectPeriod(i: number) {
    this.selectedPeriod = this.periods[i];
    this.syncEndOptions();
  }

  selectDay(i: number) {
    this.selectedDays[i] = !this.selectedDays[i];
    this.syncEndOptions();
  }

  selectEndOption(i: number) {
    this.selectedEndOption = i;
    this.syncEndOptions();
  }

  changeEndOccurances() {
    setTimeout(() => {
      let occ = this.getOccurances();
      if (occ > this.availableSessions) this.setOccurances(this.availableSessions);
      if (occ < 1) this.setOccurances(1);
      this.setLastDate(this.calcLastDate());
    });
  }

  changeEndDate() {
    setTimeout(() => {
      let dates = this.getSessionDatesByDate();
      this.setOccurances(dates.length);
    });
  }

  cancelEnter(e: any) {
    if (e.keyCode === 13) {
      e.preventDefault();
      e.stopPropagation();
    }
  }
}
